/**
 * Basis points ↔ percent (BOS-162).
 *
 * Rates cross the wire as integer **basis points** (1% = 100 bps) for the same
 * reason money crosses as integer tetri: 18% is exactly `1800`, while `0.18`
 * is not exactly anything. The conversion was written out inline in four
 * places — `taxSettingsMapping`, `settingsMeta`, `payrollRates` and
 * `dividendsMeta` — each with its own rounding, so a rate could read 18% on
 * one screen and 17.99% on another.
 */

/** Basis points in one percent. */
export const BPS_PER_PERCENT = 100;

/**
 * Basis points → percent, as a number for arithmetic.
 *
 * The division is exact enough for display (bps are small integers), but the
 * result is a float and must not be multiplied back — round-tripping goes
 * through {@link percentToBps}, which parses digits.
 */
export const bpsToPercent = (bps: number): number =>
    Math.round(bps) / BPS_PER_PERCENT;

/** Basis points in one whole (100 %). */
const BPS_PER_WHOLE = 10_000;

/**
 * Basis points → the fraction a spreadsheet's percent format shows: `1850` →
 * `0.185`, which Excel's `0.0%` renders as "18.5%". The products export
 * writes its markup cells with it (bos-app#120); like
 * {@link bpsToPercent}, the result is for display only.
 */
export const bpsToFraction = (bps: number): number =>
    Math.round(bps) / BPS_PER_WHOLE;

/**
 * Basis points → the percent string a field shows.
 *
 * `1800` → `"18"`, `1850` → `"18.50"`, `1805` → `"18.05"`. A whole percentage
 * drops the decimals entirely — `18%`, not `18.00%` — and anything else takes
 * both, which is the convention the dividends register and the payroll rates
 * already used separately. Built from the digits rather than `toFixed`, so the
 * two can no longer drift apart.
 */
export const bpsToPercentString = (bps: number): string => {
    const rounded = Math.round(bps);
    const sign = rounded < 0 ? '-' : '';
    const magnitude = Math.abs(rounded);
    const whole = Math.trunc(magnitude / BPS_PER_PERCENT);
    const frac = magnitude % BPS_PER_PERCENT;
    return frac === 0
        ? `${sign}${whole}`
        : `${sign}${whole}.${String(frac).padStart(2, '0')}`;
};

/**
 * A typed percent → integer basis points, by digit position.
 *
 * Never `value * 100`: `18.15 * 100` is `1814.9999999999998`, and a rate that
 * rounds the wrong way is a tax figure that disagrees with the ledger. Returns
 * `null` for anything that is not a percentage — empty, malformed, or finer
 * than one basis point — so a caller can tell "nothing typed" from "wrong".
 */
export const percentToBps = (input: string): number | null => {
    const cleaned = input.trim().replace(/[%\s,]/g, '');
    if (
        cleaned === '' ||
        !/^-?\d*(?:\.\d+)?$/.test(cleaned) ||
        !/\d/.test(cleaned)
    ) {
        return null;
    }

    const negative = cleaned.startsWith('-');
    const unsigned = negative ? cleaned.slice(1) : cleaned;
    const [wholeRaw = '', fracRaw = ''] = unsigned.split('.');

    // Anything past the hundredths of a percent is finer than one basis point.
    if (fracRaw.length > 2 && /[1-9]/.test(fracRaw.slice(2))) {
        return null;
    }

    const whole = wholeRaw === '' ? '0' : wholeRaw;
    const frac = fracRaw.slice(0, 2).padEnd(2, '0');
    const bps = Number(whole) * BPS_PER_PERCENT + Number(frac);
    return negative ? -bps : bps;
};

/**
 * A percent **number** → integer basis points.
 *
 * The numeric sibling of {@link percentToBps}, for the two callers that already
 * hold a number (a stepper's value, a wire field) rather than a typed string.
 * Both had their own `Math.round(percent * 100)` (BOS-171), which is the same
 * conversion written three times — and a rate that rounds differently on two
 * screens is a tax figure nobody can reconcile.
 *
 * It routes through the string parser so the digit-position rule is the same
 * one: `18.15 * 100` is `1814.9999999999998`, and `toFixed(2)` fixes that
 * before the parse sees it. A non-finite input has no basis-point reading and
 * returns `0` — the callers' previous behaviour for `NaN` was `NaN`, which
 * reached the server as `null`.
 */
export const percentNumberToBps = (percent: number): number => {
    if (!Number.isFinite(percent)) {
        return 0;
    }
    return percentToBps(percent.toFixed(2)) ?? 0;
};
